import { jsPDF } from "jspdf";

import {
  getContentBottom,
  getContentTop,
  getContentWidth,
  pdfLayout,
  pdfTheme,
} from "./theme";
import { normalizePdfText } from "./normalizePdfText";
import { renderQuestionPaperPdfDocument } from "./questionPaperPdfRenderer";
import { flattenExportSlidesForPdf } from "../ppt/flattenTeacherManualSlide";

function coerceText(value) {
  return normalizePdfText(value);
}

function hexToRgb(hex) {
  const normalized = hex.replace("#", "");
  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ];
}

function setFill(pdf, hex) {
  pdf.setFillColor(...hexToRgb(hex));
}

function setDraw(pdf, hex) {
  pdf.setDrawColor(...hexToRgb(hex));
}

function setText(pdf, hex) {
  pdf.setTextColor(...hexToRgb(hex));
}

function truncateText(pdf, text, maxWidth) {
  const value = coerceText(text);
  if (pdf.getTextWidth(value) <= maxWidth) {
    return value;
  }
  let truncated = value;
  while (truncated.length > 1 && pdf.getTextWidth(`${truncated}...`) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated}...`;
}

function createContext(pdf, document) {
  return {
    pdf,
    document,
    marginX: pdfLayout.marginX,
    contentWidth: getContentWidth(),
    contentTop: getContentTop(),
    contentBottom: getContentBottom(),
    lineHeight: pdfLayout.lineHeight,
    y: getContentTop(),
    coverRendered: false,
    contentStarted: false,
  };
}

function addPage(ctx) {
  ctx.pdf.addPage();
  ctx.y = ctx.contentTop;
}

function ensureSpace(ctx, blockHeight) {
  if (ctx.y + blockHeight > ctx.contentBottom) {
    addPage(ctx);
    return true;
  }
  return false;
}

function beginContentFlow(ctx) {
  if (ctx.contentStarted) {
    return;
  }
  ctx.contentStarted = true;
  if (ctx.coverRendered) {
    addPage(ctx);
  } else {
    ctx.y = ctx.contentTop;
  }
}

function setFont(pdf, style, size) {
  pdf.setFont("helvetica", style);
  pdf.setFontSize(size);
}

function writeLines(ctx, lines, x, lineHeight = ctx.lineHeight) {
  let { y } = ctx;
  for (const line of lines) {
    ensureSpace(ctx, lineHeight);
    y = ctx.y;
    ctx.pdf.text(line, x, y);
    ctx.y = y + lineHeight;
  }
}

function writeWrappedText(ctx, text, x, fontSize, color, options = {}) {
  const {
    fontStyle = "normal",
    maxWidth = ctx.contentWidth,
    lineHeight = ctx.lineHeight,
  } = options;

  if (!text) {
    return ctx.y;
  }

  setFont(ctx.pdf, fontStyle, fontSize);
  setText(ctx.pdf, color);
  const lines = ctx.pdf.splitTextToSize(coerceText(text), maxWidth);
  writeLines(ctx, lines, x, lineHeight);
  return ctx.y;
}

function writeBlockTitle(ctx, title) {
  if (!title) {
    return ctx.y;
  }
  ensureSpace(ctx, pdfLayout.lineHeight * 2);
  ctx.y += pdfLayout.blockGap;
  setFont(ctx.pdf, "bold", pdfLayout.typography.blockTitle);
  setText(ctx.pdf, pdfTheme.text);
  writeWrappedText(ctx, title, ctx.marginX, pdfLayout.typography.blockTitle, pdfTheme.text, {
    fontStyle: "bold",
  });
  ctx.y += pdfLayout.blockGap;
  return ctx.y;
}

function writeSectionHeading(ctx, title) {
  if (!title) {
    return ctx.y;
  }
  ensureSpace(ctx, pdfLayout.lineHeight * 3);
  ctx.y += pdfLayout.sectionGap;
  writeWrappedText(
    ctx,
    title,
    ctx.marginX,
    pdfLayout.typography.sectionTitle,
    pdfTheme.primary,
    { fontStyle: "bold", lineHeight: pdfLayout.lineHeight + 1 },
  );
  setDraw(ctx.pdf, pdfTheme.primary);
  ctx.pdf.setLineWidth(0.4);
  ctx.pdf.line(
    ctx.marginX,
    ctx.y + 1,
    ctx.marginX + ctx.contentWidth,
    ctx.y + 1,
  );
  ctx.y += pdfLayout.blockGap + 2;
  return ctx.y;
}

function writeBullets(ctx, bullets) {
  const bulletX = ctx.marginX;
  const textX = ctx.marginX + pdfLayout.bulletIndent;
  const textWidth = ctx.contentWidth - pdfLayout.bulletIndent;

  for (const bullet of bullets) {
    setFont(ctx.pdf, "normal", pdfLayout.typography.body);
    setText(ctx.pdf, pdfTheme.text);
    const lines = ctx.pdf.splitTextToSize(coerceText(bullet), textWidth);
    const blockHeight = lines.length * ctx.lineHeight + pdfLayout.blockGap;
    ensureSpace(ctx, blockHeight);

    setFont(ctx.pdf, "normal", pdfLayout.typography.body);
    setText(ctx.pdf, pdfTheme.text);
    ctx.pdf.text("-", bulletX, ctx.y);
    writeLines(ctx, lines, textX, ctx.lineHeight);
    ctx.y += pdfLayout.blockGap;
  }

  return ctx.y;
}

function estimateTableRowHeight(pdf, row, columnWidths, fontSize) {
  setFont(pdf, "normal", fontSize);
  let maxLines = 1;
  row.forEach((cell, index) => {
    const innerWidth = columnWidths[index] - pdfLayout.tableCellPaddingX * 2;
    const lines = pdf.splitTextToSize(coerceText(cell ?? ""), Math.max(innerWidth, 8));
    maxLines = Math.max(maxLines, lines.length);
  });
  return maxLines * pdfLayout.lineHeight + pdfLayout.tableRowPadding * 2;
}

function drawTableRow(ctx, row, columnWidths, y, options) {
  const { isHeader = false, fontSize } = options;
  const rowHeight = estimateTableRowHeight(ctx.pdf, row, columnWidths, fontSize);
  let x = ctx.marginX;

  setDraw(ctx.pdf, pdfTheme.border);
  ctx.pdf.setLineWidth(0.2);
  setFill(
    ctx.pdf,
    isHeader ? pdfTheme.surface : "#FFFFFF",
  );
  ctx.pdf.rect(ctx.marginX, y - pdfLayout.tableRowPadding, ctx.contentWidth, rowHeight, "FD");

  row.forEach((cell, index) => {
    const colWidth = columnWidths[index];
    setFont(ctx.pdf, isHeader ? "bold" : "normal", fontSize);
    setText(ctx.pdf, isHeader ? pdfTheme.text : pdfTheme.text);
    const innerWidth = colWidth - pdfLayout.tableCellPaddingX * 2;
    const lines = ctx.pdf.splitTextToSize(coerceText(cell), Math.max(innerWidth, 8));
    lines.forEach((line, lineIndex) => {
      ctx.pdf.text(
        line,
        x + pdfLayout.tableCellPaddingX,
        y + lineIndex * pdfLayout.lineHeight,
      );
    });

    if (index < row.length - 1) {
      ctx.pdf.line(x + colWidth, y - pdfLayout.tableRowPadding, x + colWidth, y - pdfLayout.tableRowPadding + rowHeight);
    }
    x += colWidth;
  });

  return rowHeight;
}

function writeTable(ctx, slide) {
  const headers = slide.headers ?? [];
  const rows = slide.rows ?? [];
  if (headers.length === 0 && rows.length === 0) {
    return ctx.y;
  }

  const columnCount = Math.max(headers.length, ...rows.map((row) => row.length), 1);
  const columnWidths = Array.from({ length: columnCount }, () => ctx.contentWidth / columnCount);

  if (headers.length > 0) {
    const headerHeight = estimateTableRowHeight(
      ctx.pdf,
      headers,
      columnWidths,
      pdfLayout.typography.tableHeader,
    );
    ensureSpace(ctx, headerHeight);
    const headerDrawHeight = drawTableRow(ctx, headers, columnWidths, ctx.y, {
      isHeader: true,
      fontSize: pdfLayout.typography.tableHeader,
    });
    ctx.y += headerDrawHeight;
  }

  for (const row of rows) {
    const normalizedRow = Array.from({ length: columnCount }, (_, index) => row[index] ?? "");
    const rowHeight = estimateTableRowHeight(
      ctx.pdf,
      normalizedRow,
      columnWidths,
      pdfLayout.typography.tableCell,
    );
    ensureSpace(ctx, rowHeight);
    const drawHeight = drawTableRow(ctx, normalizedRow, columnWidths, ctx.y, {
      fontSize: pdfLayout.typography.tableCell,
    });
    ctx.y += drawHeight;
  }

  ctx.y += pdfLayout.sectionGap;
  return ctx.y;
}

function renderCoverSlide(ctx, slide) {
  setFill(ctx.pdf, pdfTheme.primary);
  ctx.pdf.rect(0, 0, pdfLayout.pageWidth, 18, "F");

  ctx.y = 36;
  writeWrappedText(
    ctx,
    slide.title,
    ctx.marginX,
    pdfLayout.typography.coverTitle,
    pdfTheme.text,
    { fontStyle: "bold", lineHeight: pdfLayout.lineHeight + 2 },
  );

  if (slide.subtitle) {
    ctx.y += pdfLayout.blockGap;
    writeWrappedText(
      ctx,
      slide.subtitle,
      ctx.marginX,
      pdfLayout.typography.coverSubtitle,
      pdfTheme.muted,
      { lineHeight: pdfLayout.lineHeight + 1 },
    );
  }

  ctx.y = ctx.contentBottom;
  ctx.coverRendered = true;
}

function renderSectionSlide(ctx, slide) {
  writeSectionHeading(ctx, slide.title);
  if (slide.lead) {
    writeWrappedText(
      ctx,
      slide.lead,
      ctx.marginX,
      pdfLayout.typography.body,
      pdfTheme.text,
      { fontStyle: "bold" },
    );
    ctx.y += pdfLayout.blockGap;
  }
  if (slide.body) {
    writeWrappedText(
      ctx,
      slide.body,
      ctx.marginX,
      pdfLayout.typography.body,
      pdfTheme.text,
    );
    ctx.y += pdfLayout.blockGap;
  }
}

function renderBulletsSlide(ctx, slide) {
  writeBlockTitle(ctx, slide.title);

  if (slide.body) {
    writeWrappedText(
      ctx,
      slide.body,
      ctx.marginX,
      pdfLayout.typography.body,
      pdfTheme.text,
    );
    ctx.y += pdfLayout.blockGap;
  }

  const bullets = slide.bullets ?? [];
  if (bullets.length > 0) {
    writeBullets(ctx, bullets);
  }

  if (slide.subtitle) {
    ctx.y += pdfLayout.blockGap;
    writeWrappedText(
      ctx,
      slide.subtitle,
      ctx.marginX,
      pdfLayout.typography.caption,
      pdfTheme.muted,
      { fontStyle: "italic" },
    );
  }
}

function renderTableSlide(ctx, slide) {
  writeBlockTitle(ctx, slide.title);

  if (slide.body) {
    writeWrappedText(
      ctx,
      slide.body,
      ctx.marginX,
      pdfLayout.typography.body,
      pdfTheme.text,
    );
    ctx.y += pdfLayout.blockGap;
  }

  writeTable(ctx, slide);
}

function formatQuestionNumber(number) {
  return String(number).padStart(2, "0");
}

function drawHorizontalRule(ctx, y) {
  setDraw(ctx.pdf, pdfTheme.border);
  ctx.pdf.setLineWidth(0.3);
  ctx.pdf.line(ctx.marginX, y, ctx.marginX + ctx.contentWidth, y);
}

function renderWorksheetHeaderSlide(ctx, slide) {
  setFill(ctx.pdf, pdfTheme.primary);
  ctx.pdf.rect(0, 0, pdfLayout.pageWidth, 3, "F");

  ctx.y = 16;
  writeWrappedText(
    ctx,
    slide.title,
    ctx.marginX,
    pdfLayout.typography.worksheetTitle,
    pdfTheme.text,
    { fontStyle: "bold", lineHeight: pdfLayout.lineHeight + 2 },
  );

  if (slide.curriculumLabel) {
    ctx.y += 2;
    writeWrappedText(
      ctx,
      slide.curriculumLabel,
      ctx.marginX,
      pdfLayout.typography.worksheetMeta,
      pdfTheme.muted,
    );
  }

  if (slide.subtitle) {
    ctx.y += 1;
    writeWrappedText(
      ctx,
      slide.subtitle,
      ctx.marginX,
      pdfLayout.typography.worksheetMeta,
      pdfTheme.text,
      { lineHeight: pdfLayout.lineHeight + 0.5 },
    );
  }

  ctx.y += pdfLayout.worksheet.headerRuleGap;
  drawHorizontalRule(ctx, ctx.y);
  ctx.y += pdfLayout.worksheet.nameLineGap;

  setFont(ctx.pdf, "normal", pdfLayout.typography.worksheetMeta);
  setText(ctx.pdf, pdfTheme.muted);
  ctx.pdf.text("Name: ________________________________", ctx.marginX, ctx.y);
  ctx.pdf.text(
    "Date: ______________",
    ctx.marginX + ctx.contentWidth * 0.62,
    ctx.y,
  );

  ctx.y += pdfLayout.worksheet.headerRuleGap + 2;
  if (slide.questionCount > 0) {
    writeWrappedText(
      ctx,
      `${slide.questionCount} questions`,
      ctx.marginX,
      pdfLayout.typography.caption,
      pdfTheme.muted,
    );
    ctx.y += pdfLayout.blockGap;
  }

  ctx.y += pdfLayout.worksheet.questionGap;
}

function writeQuestionOption(ctx, label) {
  const optionX = ctx.marginX + pdfLayout.worksheet.numberColumnWidth + 2;
  const textWidth = ctx.contentWidth - pdfLayout.worksheet.numberColumnWidth - 2;
  const lines = ctx.pdf.splitTextToSize(coerceText(label), textWidth);
  const blockHeight = lines.length * ctx.lineHeight + pdfLayout.worksheet.optionGap;
  ensureSpace(ctx, blockHeight);

  setFont(ctx.pdf, "normal", pdfLayout.typography.questionOption);
  setText(ctx.pdf, pdfTheme.text);
  writeLines(ctx, lines, optionX, ctx.lineHeight);
  ctx.y += pdfLayout.worksheet.optionGap;
}

function renderMcqOptions(ctx, choices) {
  for (const choice of choices) {
    writeQuestionOption(ctx, choice.label);
  }
}

function renderShortAnswerLines(ctx, lineCount) {
  const count = Math.max(1, lineCount ?? 3);
  for (let index = 0; index < count; index += 1) {
    ensureSpace(ctx, pdfLayout.lineHeight * 2);
    ctx.y += pdfLayout.lineHeight;
    drawHorizontalRule(ctx, ctx.y);
    ctx.y += pdfLayout.worksheet.optionGap;
  }
}

function renderTrueFalseOptions(ctx) {
  writeQuestionOption(ctx, "( ) True");
  writeQuestionOption(ctx, "( ) False");
}

function renderMatchingPairs(ctx, pairs) {
  if (!pairs.length) {
    return;
  }

  const contentX = ctx.marginX + pdfLayout.worksheet.numberColumnWidth + 2;
  const availableWidth = ctx.contentWidth - pdfLayout.worksheet.numberColumnWidth - 2;
  const columnWidth = availableWidth / 2 - 2;
  const rightX = contentX + columnWidth + 4;
  const textWidth = Math.max(columnWidth - 2, 8);

  const leftItems = pairs.map(
    (pair, index) => `${index + 1}. ${coerceText(pair.left)}`,
  );
  const rightItems = [...pairs]
    .map((pair) => coerceText(pair.right))
    .sort((left, right) => left.localeCompare(right))
    .map((item, index) => `${String.fromCharCode(65 + index)}. ${item}`);

  ensureSpace(ctx, pdfLayout.lineHeight * 2);
  setFont(ctx.pdf, "bold", pdfLayout.typography.caption);
  setText(ctx.pdf, pdfTheme.muted);
  ctx.pdf.text("Column A", contentX, ctx.y);
  ctx.pdf.text("Column B", rightX, ctx.y);
  ctx.y += pdfLayout.lineHeight + 1;

  setFont(ctx.pdf, "normal", pdfLayout.typography.questionOption);
  setText(ctx.pdf, pdfTheme.text);

  const rowCount = Math.max(leftItems.length, rightItems.length);
  for (let index = 0; index < rowCount; index += 1) {
    const leftLines = ctx.pdf.splitTextToSize(leftItems[index] ?? "", textWidth);
    const rightLines = ctx.pdf.splitTextToSize(rightItems[index] ?? "", textWidth);
    const rowHeight =
      Math.max(leftLines.length, rightLines.length) * ctx.lineHeight +
      pdfLayout.worksheet.optionGap;

    ensureSpace(ctx, rowHeight);
    const drawY = ctx.y;

    leftLines.forEach((line, lineIndex) => {
      ctx.pdf.text(line, contentX, drawY + lineIndex * ctx.lineHeight);
    });
    rightLines.forEach((line, lineIndex) => {
      ctx.pdf.text(line, rightX, drawY + lineIndex * ctx.lineHeight);
    });

    ctx.y = drawY + rowHeight;
  }

  ctx.y += pdfLayout.blockGap;

  writeWrappedText(
    ctx,
    "Write the matching letter next to each number.",
    contentX,
    pdfLayout.typography.caption,
    pdfTheme.muted,
    {
      maxWidth: availableWidth,
      fontStyle: "italic",
    },
  );
  ctx.y += pdfLayout.blockGap;
}

function renderQuestionSlide(ctx, slide) {
  const numberLabel = formatQuestionNumber(slide.number);
  const numberX = ctx.marginX;
  const contentX = ctx.marginX + pdfLayout.worksheet.numberColumnWidth + 2;
  const promptWidth = ctx.contentWidth - pdfLayout.worksheet.numberColumnWidth - 2;

  ensureSpace(ctx, pdfLayout.lineHeight * 4);

  setFont(ctx.pdf, "bold", pdfLayout.typography.questionNumber);
  setText(ctx.pdf, pdfTheme.primary);
  ctx.pdf.text(numberLabel, numberX, ctx.y);

  setFont(ctx.pdf, "bold", pdfLayout.typography.questionPrompt);
  setText(ctx.pdf, pdfTheme.text);
  const promptLines = ctx.pdf.splitTextToSize(coerceText(slide.prompt), promptWidth);
  writeLines(ctx, promptLines, contentX, ctx.lineHeight + 0.5);
  ctx.y += pdfLayout.blockGap;

  if (slide.questionType !== "mcq" && slide.questionTypeLabel) {
    setFont(ctx.pdf, "normal", pdfLayout.typography.caption);
    setText(ctx.pdf, pdfTheme.muted);
    ctx.pdf.text(slide.questionTypeLabel, contentX, ctx.y);
    ctx.y += pdfLayout.lineHeight + 1;
  }

  switch (slide.questionType) {
    case "short-answer":
      renderShortAnswerLines(ctx, slide.answerLines);
      break;
    case "true-false":
      renderTrueFalseOptions(ctx);
      break;
    case "matching":
      renderMatchingPairs(ctx, slide.matchingPairs ?? []);
      break;
    case "fill-blanks":
      writeWrappedText(
        ctx,
        slide.blankPrompt,
        contentX,
        pdfLayout.typography.questionOption,
        pdfTheme.text,
      );
      renderShortAnswerLines(ctx, 2);
      break;
    case "mcq":
    default:
      renderMcqOptions(ctx, slide.choices ?? []);
      break;
  }

  ctx.y += pdfLayout.worksheet.questionGap;
  drawHorizontalRule(ctx, ctx.y);
  ctx.y += pdfLayout.worksheet.questionGap;
}

function renderAnswerEntrySlide(ctx, slide) {
  const numberLabel = formatQuestionNumber(slide.number);
  ensureSpace(ctx, pdfLayout.lineHeight * 3);

  setFont(ctx.pdf, "bold", pdfLayout.typography.questionNumber);
  setText(ctx.pdf, pdfTheme.primary);
  ctx.pdf.text(numberLabel, ctx.marginX, ctx.y);

  const textX = ctx.marginX + pdfLayout.worksheet.numberColumnWidth + 2;
  const textWidth = ctx.contentWidth - pdfLayout.worksheet.numberColumnWidth - 2;

  if (slide.matchingPairs?.length) {
    for (const pair of slide.matchingPairs) {
      writeWrappedText(
        ctx,
        `${pair.left} -> ${pair.right}`,
        textX,
        pdfLayout.typography.answerEntry,
        pdfTheme.text,
        { maxWidth: textWidth },
      );
    }
  } else if (slide.blankAnswers?.length) {
    slide.blankAnswers.forEach((answer, index) => {
      writeWrappedText(
        ctx,
        `Blank ${index + 1}: ${answer}`,
        textX,
        pdfLayout.typography.answerEntry,
        pdfTheme.text,
        { maxWidth: textWidth },
      );
    });
  } else if (slide.correctAnswer) {
    writeWrappedText(
      ctx,
      slide.correctAnswer,
      textX,
      pdfLayout.typography.answerEntry,
      pdfTheme.text,
      { maxWidth: textWidth, fontStyle: "bold" },
    );
  }

  if (slide.explanation) {
    writeWrappedText(
      ctx,
      slide.explanation,
      textX,
      pdfLayout.typography.caption,
      pdfTheme.muted,
      { maxWidth: textWidth },
    );
  }

  if (slide.markingGuidance) {
    writeWrappedText(
      ctx,
      slide.markingGuidance,
      textX,
      pdfLayout.typography.caption,
      pdfTheme.muted,
      { maxWidth: textWidth, fontStyle: "italic" },
    );
  }

  ctx.y += pdfLayout.blockGap;
}

function renderContentSlide(ctx, slide) {
  switch (slide.kind) {
    case "worksheet-header":
      renderWorksheetHeaderSlide(ctx, slide);
      break;
    case "question":
      renderQuestionSlide(ctx, slide);
      break;
    case "answer-entry":
      renderAnswerEntrySlide(ctx, slide);
      break;
    case "section":
      renderSectionSlide(ctx, slide);
      break;
    case "bullets":
      renderBulletsSlide(ctx, slide);
      break;
    case "table":
      renderTableSlide(ctx, slide);
      break;
    default:
      break;
  }
}

function drawRunningHeader(pdf, document) {
  setFill(pdf, pdfTheme.primary);
  pdf.rect(0, 0, pdfLayout.pageWidth, 2.5, "F");

  setFont(pdf, "bold", pdfLayout.typography.headerLabel);
  setText(pdf, pdfTheme.muted);
  const headerLabel = truncateText(
    pdf,
    document.title ?? document.filename ?? pdfTheme.brandName,
    getContentWidth() * 0.55,
  );
  pdf.text(headerLabel, pdfLayout.marginX, 8);

  setFont(pdf, "bold", pdfLayout.typography.headerLabel);
  setText(pdf, pdfTheme.primary);
  pdf.text(pdfTheme.brandName, pdfLayout.pageWidth - pdfLayout.marginX, 8, {
    align: "right",
  });
}

function drawRunningFooter(pdf, document, pageNumber, totalPages) {
  const footerY = pdfLayout.pageHeight - pdfLayout.marginBottom;
  setDraw(pdf, pdfTheme.border);
  pdf.setLineWidth(0.2);
  pdf.line(
    pdfLayout.marginX,
    footerY - 5,
    pdfLayout.pageWidth - pdfLayout.marginX,
    footerY - 5,
  );

  setFont(pdf, "normal", pdfLayout.typography.footerLabel);
  setText(pdf, pdfTheme.muted);

  const subject = document.subject ? String(document.subject) : "";
  if (subject) {
    pdf.text(
      truncateText(pdf, subject, getContentWidth() * 0.45),
      pdfLayout.marginX,
      footerY,
    );
  }

  pdf.text(
    `Page ${pageNumber} of ${totalPages}`,
    pdfLayout.pageWidth / 2,
    footerY,
    { align: "center" },
  );

  pdf.text(
    pdfTheme.brandName,
    pdfLayout.pageWidth - pdfLayout.marginX,
    footerY,
    { align: "right" },
  );
}

function drawCoverFooter(pdf, document, pageNumber, totalPages) {
  const footerY = pdfLayout.pageHeight - pdfLayout.marginBottom;
  setFont(pdf, "normal", pdfLayout.typography.footerLabel);
  setText(pdf, pdfTheme.muted);
  pdf.text(pdfTheme.brandName, pdfLayout.marginX, footerY);
  pdf.text(
    `Page ${pageNumber} of ${totalPages}`,
    pdfLayout.pageWidth - pdfLayout.marginX,
    footerY,
    { align: "right" },
  );
  if (document.subject) {
    pdf.text(
      truncateText(pdf, document.subject, getContentWidth() * 0.5),
      pdfLayout.pageWidth / 2,
      footerY,
      { align: "center" },
    );
  }
}

function drawWorksheetRunningHeader(pdf, document) {
  setFill(pdf, pdfTheme.primary);
  pdf.rect(0, 0, pdfLayout.pageWidth, 1.5, "F");

  setFont(pdf, "normal", pdfLayout.typography.headerLabel);
  setText(pdf, pdfTheme.muted);
  const subject = document.subject ? coerceText(document.subject) : "";
  if (subject) {
    pdf.text(
      truncateText(pdf, subject, getContentWidth() * 0.55),
      pdfLayout.marginX,
      7,
    );
  }

  setFont(pdf, "bold", pdfLayout.typography.headerLabel);
  setText(pdf, pdfTheme.primary);
  pdf.text("Practice Sheets", pdfLayout.pageWidth - pdfLayout.marginX, 7, {
    align: "right",
  });
}

function drawWorksheetRunningFooter(pdf, document, pageNumber, totalPages) {
  const footerY = pdfLayout.pageHeight - pdfLayout.marginBottom;
  setDraw(pdf, pdfTheme.border);
  pdf.setLineWidth(0.2);
  pdf.line(
    pdfLayout.marginX,
    footerY - 4,
    pdfLayout.pageWidth - pdfLayout.marginX,
    footerY - 4,
  );

  setFont(pdf, "normal", pdfLayout.typography.footerLabel);
  setText(pdf, pdfTheme.muted);
  pdf.text(
    truncateText(pdf, document.title ?? document.filename ?? "Worksheet", getContentWidth() * 0.45),
    pdfLayout.marginX,
    footerY,
  );
  pdf.text(
    `Page ${pageNumber} of ${totalPages}`,
    pdfLayout.pageWidth / 2,
    footerY,
    { align: "center" },
  );
}

function applyWorksheetPageChrome(pdf, document) {
  const totalPages = pdf.getNumberOfPages();

  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    if (page === 1) {
      drawWorksheetRunningFooter(pdf, document, page, totalPages);
    } else {
      drawWorksheetRunningHeader(pdf, document);
      drawWorksheetRunningFooter(pdf, document, page, totalPages);
    }
  }
}

function applyPageChrome(pdf, document) {
  if (document.layout === "worksheet") {
    applyWorksheetPageChrome(pdf, document);
    return;
  }

  const totalPages = pdf.getNumberOfPages();
  const opensWithCover =
    document.slides?.[0]?.kind === "title" || document.slides?.[0]?.kind === "cover";

  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    if (opensWithCover && page === 1) {
      drawCoverFooter(pdf, document, page, totalPages);
    } else {
      drawRunningHeader(pdf, document);
      drawRunningFooter(pdf, document, page, totalPages);
    }
  }
}

/**
 * Render a structured export document to a print-ready A4 PDF.
 *
 * Slide kinds: `title` (cover), `section`, `bullets`, `table`.
 * Content flows continuously across pages with running headers/footers.
 */
export function renderPdfDocument(document) {
  if (document.layout === "question-paper") {
    return renderQuestionPaperPdfDocument(document);
  }

  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  pdf.setProperties({
    title: document.title,
    subject: document.subject,
    author: document.author ?? "Tema",
    creator: pdfTheme.brandName,
  });

  const ctx = createContext(pdf, document);
  const slides = flattenExportSlidesForPdf(document.slides ?? []);
  const isWorksheet = document.layout === "worksheet";

  slides.forEach((slide, index) => {
    if (slide.kind === "title") {
      if (index > 0) {
        addPage(ctx);
      }
      renderCoverSlide(ctx, slide);
      return;
    }

    if (isWorksheet && slide.kind === "worksheet-header") {
      if (index > 0) {
        addPage(ctx);
      }
      ctx.contentStarted = true;
      ctx.y = 12;
      renderWorksheetHeaderSlide(ctx, slide);
      return;
    }

    beginContentFlow(ctx);

    if (slide.pageBreakBefore) {
      addPage(ctx);
    }

    renderContentSlide(ctx, slide);
  });

  applyPageChrome(pdf, document);
  return pdf;
}
